import type { FC, ReactNode } from 'react'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ConversationEventDetails, UUID } from '@/index'
import { CLAUDE_CODE_CONVERSATION_EVENT, EventType, HookType } from '@/index'
import type { FormattedAgentActivity, FormattedUserActivity } from '@/features/events/shared'
import {
  AgentMode,
  agentModeFrom,
  formatAgentActivityEvent,
  formatMcpToolForDisplay,
} from '@/features/events/shared'
import { normalizeForComparison } from '@/common/utils'
import { buildClaudeCodeEventDetailsFrom } from '@/features/events/conversation/models'
import type { ConversationEventPayload } from '@/infra/sse/conversation/payloads'
import type {
  SessionSeed,
  ClaudeSession,
  ConversationTurn,
  ConversationTurnEntry,
  UserAnswersDraft,
  Command,
  UserInputDraft,
} from './types'
import { ENCRYPTED_PLACEHOLDER } from '@/infra/sse/shared/decryption'
import { TRANSIENT_SESSION_EVENT_TYPE } from '@/infra/sse/constants'
import type { AgentActivityEventPayload } from '@/infra/sse/activity/payloads'
import type { TmuxSession } from '@/infra/sse/shared/payloads'
import {
  buildSessionBreadcrumb,
  claudeSessionFrom,
  clearClaudeSessionMemory,
  conversationTurnEntryFrom,
  conversationTurnFrom,
  createSession,
  ErrorRecoveryType,
  formatTmuxSessionName,
  isErrorRecovery,
  isInterrupt,
  isPrompt,
  isQuestionnaireAnswer,
  isResume,
  isUserOptionSelection,
  withUserAnswersDraft,
  withUserInputDrafts,
} from './types'

interface SessionStateContextValue {
  readonly sessions: ClaudeSession[]
  readonly recordCommand: (sessionId: UUID, userPromptId: UUID, userCommand: Command) => void
  readonly createNewTurn: (session: ClaudeSession, userPromptId: UUID, userCommand: Command) => void
  readonly deleteSessions: (sessionIds: UUID[]) => void
  readonly deleteSessionsByProject: (projectKey: string) => void
  readonly deleteSessionsByHost: (hostname: string) => void
  readonly updateUserInputDrafts: (sessionId: UUID, drafts: UserInputDraft[]) => void
  readonly updateUserAnswersDraft: (sessionId: UUID, draft: UserAnswersDraft | null) => void
}

const SessionStateContext = createContext<SessionStateContextValue | null>(null)

const ACTIVITY_CLEARING_EVENT_TYPES = new Set([
  EventType.TOOL_PERMISSION_REQUEST,
  EventType.PLAN_APPROVAL_REQUEST,
  EventType.AGENT_QUESTIONS,
  EventType.AGENT_REPLY,
  EventType.FILE_MODIFICATION,
  EventType.AGENT_NARRATIVE,
])

interface SessionStateProviderProps {
  readonly children: ReactNode
}

export const SessionStateProvider: FC<SessionStateProviderProps> = ({ children }) => {
  const sessionsRef = useRef(new Map<UUID, ClaudeSession>())
  const [sessions, setSessions] = useState<ClaudeSession[]>([])

  const processClaudeCodeEvent = useCallback((event: ConversationEventDetails) => {
    const sessionId = event.eventMetadata.claudeSessionId
    const session = getOrCreateSession(sessionsRef.current, sessionId, event)
    session.isTransient = false
    const turnId = event.eventMetadata.userPromptId

    if (event.eventType === EventType.USER_PROMPT) {
      if (isQuestionnaireAnswersPayload(event.userPrompt)) {
        return
      }

      const turn = session.conversationTurns.get(turnId)
      if (turn) {
        const isDuplicate = turn.userCommands.some(
          (cmd) =>
            cmd.userPrompt !== null &&
            event.userPrompt !== null &&
            normalizeForComparison(cmd.userPrompt) === normalizeForComparison(event.userPrompt),
        )

        if (isDuplicate) {
          return
        }
      }
    }

    const isNewTurn = !session.conversationTurns.has(turnId)
    const turn = getOrCreateTurn(session, turnId, event)
    const conversationEvent = conversationTurnEntryFrom(event)
    const updatedTurn = addEventToTurn(
      turn,
      conversationEvent,
      event.eventMetadata.hookType,
      event.eventMetadata.timestamp,
    )

    session.conversationTurns.set(turnId, updatedTurn)

    if (isNewTurn) {
      session.runtime.memory = clearClaudeSessionMemory()
    }

    session.currentAgentMode = event.agentMode

    if (session.runtime.pendingAgentMode === event.agentMode) {
      session.runtime.pendingAgentMode = null
    }

    if (ACTIVITY_CLEARING_EVENT_TYPES.has(event.eventType)) {
      session.runtime.agentActivity = null
      session.runtime.latestActivity = null
      session.runtime.userActivity = null
    }

    updateSessionsState(sessionsRef, sessionId, session, setSessions)
  }, [])

  const processTransientSessionEvent = useCallback((payload: ConversationEventPayload) => {
    const sessionId = payload.eventMetadata.claudeSessionId
    const existingSession = sessionsRef.current.get(sessionId)

    if (existingSession) return

    const seed: SessionSeed = {
      claudeSessionId: sessionId,
      userExternalId: payload.eventMetadata.userExternalId,
      transcriptPath: payload.eventMetadata.transcriptPath,
      contextWorkDirectory: payload.eventMetadata.contextWorkDirectory,
      dangerouslySkipPermissions: payload.eventMetadata.dangerouslySkipPermissions,
      autoModeSupported: payload.eventMetadata.autoModeSupported,
      agentMode: agentModeFrom(payload.eventMetadata.agentMode),
      tmuxSession: payload.telemetry?.tmuxSession ?? null,
      hostDetails: payload.hostDetails,
      isTransient: true,
    }

    const session = createSession(seed)
    updateSessionsState(sessionsRef, sessionId, session, setSessions)
  }, [])

  useEffect(() => {
    const handleEventPayload = (customEvent: CustomEvent) => {
      const eventPayload = customEvent.detail as ConversationEventPayload

      if (eventPayload.eventType === TRANSIENT_SESSION_EVENT_TYPE) {
        processTransientSessionEvent(eventPayload)
        return
      }

      const claudeCodeEvent = buildClaudeCodeEventDetailsFrom(eventPayload)
      processClaudeCodeEvent(claudeCodeEvent)
    }

    globalThis.addEventListener(CLAUDE_CODE_CONVERSATION_EVENT, handleEventPayload as EventListener)
    return () =>
      globalThis.removeEventListener(
        CLAUDE_CODE_CONVERSATION_EVENT,
        handleEventPayload as EventListener,
      )
  }, [processClaudeCodeEvent, processTransientSessionEvent])

  useEffect(() => {
    const handleActivityEvent = (customEvent: CustomEvent<AgentActivityEventPayload>) => {
      const payload = customEvent.detail
      const sessionId = payload.eventMetadata.claudeSessionId
      const formatted = formatAgentActivityEvent(payload)

      if (!formatted) return

      const session =
        sessionsRef.current.get(sessionId) ??
        createSession({
          claudeSessionId: sessionId,
          userExternalId: payload.eventMetadata.userExternalId,
          transcriptPath: payload.eventMetadata.transcriptPath,
          contextWorkDirectory: payload.eventMetadata.contextWorkDirectory,
          dangerouslySkipPermissions: payload.eventMetadata.dangerouslySkipPermissions,
          autoModeSupported: payload.eventMetadata.autoModeSupported,
          agentMode: agentModeFrom(payload.eventMetadata.agentMode),
          tmuxSession: payload.telemetry.tmuxSession,
          hostDetails: payload.hostDetails,
        })

      const updatedSession = cloneSession(session)

      const tmuxSessionUpdated = applyTmuxSessionUpdate(
        updatedSession,
        payload.telemetry.tmuxSession,
        session.tmuxSession,
      )

      if (tmuxSessionUpdated) {
        const resolvedWorkDir =
          updatedSession.contextWorkDirectory === ENCRYPTED_PLACEHOLDER
            ? session.contextWorkDirectory
            : updatedSession.contextWorkDirectory

        updatedSession.sessionBreadcrumb = buildSessionBreadcrumb(
          updatedSession.sessionName,
          updatedSession.userHostDetails.hostName,
          resolvedWorkDir,
          updatedSession.sessionId,
        )
      }

      const { userActivity, agentActivity } = resolveActivityState(
        formatted,
        session.runtime.userActivity,
      )

      updatedSession.runtime.userActivity = userActivity
      updatedSession.runtime.agentActivity = agentActivity
      updatedSession.runtime.latestActivity = formatted
      updatedSession.currentAgentMode = formatted.agentMode

      if (updatedSession.runtime.pendingAgentMode === formatted.agentMode) {
        updatedSession.runtime.pendingAgentMode = null
      }

      updateSessionsState(sessionsRef, sessionId, updatedSession, setSessions)
    }

    globalThis.addEventListener('activity-event', handleActivityEvent as EventListener)
    return () =>
      globalThis.removeEventListener('activity-event', handleActivityEvent as EventListener)
  }, [])

  const recordCommand = useCallback((sessionId: UUID, userPromptId: UUID, userCommand: Command) => {
    const session = sessionsRef.current.get(sessionId)
    if (!session) {
      return
    }

    const updatedSession = cloneSession(session)

    const turn = session.conversationTurns.get(userPromptId)
    if (turn) {
      const isDuplicate = turn.userCommands.some(
        (c) => c.refEventId === userCommand.refEventId && c.commandType === userCommand.commandType,
      )
      if (isDuplicate) return

      const updatedTurn = { ...turn, userCommands: [...turn.userCommands, userCommand] }
      updatedSession.conversationTurns.set(userPromptId, updatedTurn)
    }

    const currentMode = session.currentAgentMode
    const targetMode = userCommand.agentMode ?? currentMode

    if (userCommand.agentMode !== null && userCommand.agentMode !== session.currentAgentMode) {
      updatedSession.runtime.pendingAgentMode = userCommand.agentMode
    }

    let activity = buildUserActivity(userCommand, targetMode)

    if (isUserOptionSelection(userCommand) && turn) {
      const refEvent = turn.events.find((e) => e.eventId === userCommand.refEventId)
      activity = buildUserOptionActivity(userCommand, refEvent ?? null, targetMode) ?? activity
    }

    if (activity) {
      updatedSession.runtime.userActivity = activity
      updatedSession.runtime.latestActivity = activity
    }

    updateSessionsState(sessionsRef, sessionId, updatedSession, setSessions)
  }, [])

  const deleteSessions = useCallback((sessionIds: UUID[]) => {
    const sessionsMap = new Map(sessionsRef.current)

    for (const sessionId of sessionIds) {
      sessionsMap.delete(sessionId)
    }

    sessionsRef.current = sessionsMap
    setSessions(Array.from(sessionsMap.values()))
  }, [])

  const deleteSessionsByProject = useCallback(
    (projectKey: string) => {
      const sessionsToDelete = Array.from(sessionsRef.current.values())
        .filter((session) => (session.contextWorkDirectory ?? 'ungrouped') === projectKey)
        .map((session) => session.sessionId)

      if (sessionsToDelete.length > 0) {
        deleteSessions(sessionsToDelete)
      }
    },
    [deleteSessions],
  )

  const deleteSessionsByHost = useCallback(
    (hostname: string) => {
      const sessionsToDelete = Array.from(sessionsRef.current.values())
        .filter((session) => session.userHostDetails.hostName === hostname)
        .map((session) => session.sessionId)

      if (sessionsToDelete.length > 0) {
        deleteSessions(sessionsToDelete)
      }
    },
    [deleteSessions],
  )

  const updateUserInputDrafts = useCallback((sessionId: UUID, drafts: UserInputDraft[]) => {
    const session = sessionsRef.current.get(sessionId)
    if (!session) {
      return
    }

    const updatedSession = cloneSession(session)
    updatedSession.runtime.memory = withUserInputDrafts(session.runtime.memory, drafts)

    updateSessionsState(sessionsRef, sessionId, updatedSession, setSessions)
  }, [])

  const updateUserAnswersDraft = useCallback((sessionId: UUID, draft: UserAnswersDraft | null) => {
    const session = sessionsRef.current.get(sessionId)
    if (!session) {
      return
    }

    const updatedSession = cloneSession(session)
    updatedSession.runtime.memory = withUserAnswersDraft(session.runtime.memory, draft)

    updateSessionsState(sessionsRef, sessionId, updatedSession, setSessions)
  }, [])

  const createNewTurn = useCallback(
    (session: ClaudeSession, newUserPromptId: UUID, userCommand: Command) => {
      const newTurn: ConversationTurn = {
        userPromptId: newUserPromptId,
        startTime: userCommand.timestamp,
        endTime: null,
        events: [],
        userCommands: [userCommand],
      }

      const updatedSession = cloneSession(session)
      updatedSession.conversationTurns.set(newUserPromptId, newTurn)
      updatedSession.runtime.memory = clearClaudeSessionMemory()

      if (userCommand.agentMode !== null && userCommand.agentMode !== session.currentAgentMode) {
        updatedSession.runtime.pendingAgentMode = userCommand.agentMode
      }

      const currentMode = session.currentAgentMode
      updatedSession.runtime.userActivity = {
        toolName: '',
        activityContext: 'Processing your reply',
        status: 'user',
        isBlockingResponse: true,
        agentMode: userCommand.agentMode ?? currentMode,
      }
      updatedSession.runtime.latestActivity = updatedSession.runtime.userActivity

      updateSessionsState(sessionsRef, session.sessionId, updatedSession, setSessions)
    },
    [],
  )

  const value = useMemo<SessionStateContextValue>(
    () => ({
      sessions,
      recordCommand,
      createNewTurn,
      deleteSessions,
      deleteSessionsByProject,
      deleteSessionsByHost,
      updateUserInputDrafts,
      updateUserAnswersDraft,
    }),
    [
      sessions,
      recordCommand,
      createNewTurn,
      deleteSessions,
      deleteSessionsByProject,
      deleteSessionsByHost,
      updateUserInputDrafts,
      updateUserAnswersDraft,
    ],
  )

  return <SessionStateContext.Provider value={value}>{children}</SessionStateContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSessionStateContext(): SessionStateContextValue {
  const context = useContext(SessionStateContext)
  if (!context) {
    throw new Error('useSessionStateContext must be used within SessionStateProvider')
  }
  return context
}

function getOrCreateSession(
  sessionsMap: Map<UUID, ClaudeSession>,
  sessionId: UUID,
  event: ConversationEventDetails,
): ClaudeSession {
  const existingSession = sessionsMap.get(sessionId)

  if (existingSession) {
    return updateSessionIfNeeded(existingSession, event)
  }

  return claudeSessionFrom(event)
}

function updateSessionIfNeeded(
  session: ClaudeSession,
  event: ConversationEventDetails,
): ClaudeSession {
  const cloned = cloneSession(session)
  let breadcrumbDirty = false

  if (applyTmuxSessionUpdate(cloned, event.telemetry.tmuxSession, session.tmuxSession)) {
    breadcrumbDirty = true
  }

  // Heal stale metadata from activity-seeded sessions where crypto wasn't ready
  const newContextWorkDirectory = event.eventMetadata.contextWorkDirectory
  if (
    session.contextWorkDirectory === ENCRYPTED_PLACEHOLDER &&
    newContextWorkDirectory !== ENCRYPTED_PLACEHOLDER
  ) {
    cloned.contextWorkDirectory = newContextWorkDirectory
    breadcrumbDirty = true
  }

  const newTranscriptPath = event.eventMetadata.transcriptPath
  if (
    session.transcriptPath === ENCRYPTED_PLACEHOLDER &&
    newTranscriptPath !== ENCRYPTED_PLACEHOLDER
  ) {
    cloned.transcriptPath = newTranscriptPath
  }

  if (breadcrumbDirty) {
    cloned.sessionBreadcrumb = buildSessionBreadcrumb(
      cloned.sessionName,
      cloned.userHostDetails.hostName,
      cloned.contextWorkDirectory,
      cloned.sessionId,
    )
  }

  return cloned
}

function applyTmuxSessionUpdate(
  target: ClaudeSession,
  newTmuxSession: TmuxSession | null | undefined,
  currentTmuxSession: TmuxSession | null,
): boolean {
  const newSessionId = newTmuxSession?.sessionId
  const newSessionName = newTmuxSession?.sessionName

  const tmuxSessionPresent = newSessionId !== undefined
  const isTmuxSessionChanged = tmuxSessionPresent && newSessionId !== currentTmuxSession?.sessionId
  const isTmuxSessionRenamed =
    tmuxSessionPresent && !!newSessionName && newSessionName !== currentTmuxSession?.sessionName

  if (!isTmuxSessionChanged && !isTmuxSessionRenamed) {
    return false
  }

  target.sessionName = formatTmuxSessionName(newSessionName ?? '')
  target.tmuxSession = newTmuxSession ?? null
  return true
}

function getOrCreateTurn(
  session: ClaudeSession,
  turnId: UUID,
  event: ConversationEventDetails,
): ConversationTurn {
  const existingTurn = session.conversationTurns.get(turnId)

  if (existingTurn) {
    return existingTurn
  }

  return conversationTurnFrom(event)
}

function addEventToTurn(
  turn: ConversationTurn,
  event: ConversationTurnEntry,
  hookType: HookType | null,
  timestamp: string,
): ConversationTurn {
  if (turn.events.some((e) => e.eventId === event.eventId)) {
    return turn
  }
  return {
    ...turn,
    events: [...turn.events, event],
    endTime: hookType === HookType.STOP ? timestamp : turn.endTime,
  }
}

function cloneSession(session: ClaudeSession): ClaudeSession {
  return {
    ...session,
    conversationTurns: new Map(session.conversationTurns),
    runtime: { ...session.runtime },
  }
}

function updateSessionsState(
  sessionsRef: React.RefObject<Map<UUID, ClaudeSession>>,
  sessionId: UUID,
  session: ClaudeSession,
  setSessions: React.Dispatch<React.SetStateAction<ClaudeSession[]>>,
): void {
  const sessionsMap = new Map(sessionsRef.current)
  sessionsMap.set(sessionId, session)
  sessionsRef.current = sessionsMap
  setSessions(Array.from(sessionsMap.values()))
}

function isQuestionnaireAnswersPayload(userPromptString: string | null): boolean {
  if (!userPromptString) return false

  try {
    const parsed = JSON.parse(userPromptString)
    return typeof parsed === 'object' && parsed !== null && 'answers' in parsed
  } catch {
    return false
  }
}

function buildUserActivity(
  userCommand: Command,
  agentMode: AgentMode,
): FormattedUserActivity | null {
  if (isInterrupt(userCommand)) {
    return {
      toolName: '',
      activityContext: 'Claude Code interrupted',
      status: 'user',
      isBlockingResponse: false,
      agentMode,
    }
  }

  if (isResume(userCommand)) {
    return {
      toolName: '',
      activityContext: 'Claude Code resumed',
      status: 'user',
      isBlockingResponse: false,
      agentMode,
    }
  }

  if (isErrorRecovery(userCommand)) {
    const isApiError = userCommand.errorRecovery === ErrorRecoveryType.API_ERROR_RECOVERY
    return {
      toolName: '',
      activityContext: isApiError
        ? 'Recovering from Claude Code API error'
        : 'Recovering from agent dialogue internal error',
      status: 'user',
      isBlockingResponse: false,
      agentMode,
    }
  }

  if (isQuestionnaireAnswer(userCommand)) {
    return {
      toolName: '',
      activityContext: 'Processing your answers',
      status: 'user',
      isBlockingResponse: true,
      agentMode,
      pendingAcknowledgement: {
        eventType: EventType.AGENT_QUESTIONS,
        toolName: null,
      },
    }
  }

  if (isPrompt(userCommand)) {
    return {
      toolName: '',
      activityContext: 'Processing your injected prompt',
      status: 'user',
      isBlockingResponse: false,
      agentMode,
    }
  }

  return null
}

function buildUserOptionActivity(
  userCommand: Command,
  refEvent: ConversationTurnEntry | null,
  agentMode: AgentMode,
): FormattedUserActivity | null {
  if (!isUserOptionSelection(userCommand)) return null

  if (refEvent?.eventType === EventType.PLAN_APPROVAL_REQUEST) {
    return {
      toolName: '',
      activityContext: 'Processing plan request',
      status: 'user',
      isBlockingResponse: true,
      agentMode,
      pendingAcknowledgement: {
        eventType: EventType.PLAN_APPROVAL_REQUEST,
        toolName: null,
      },
    }
  }

  if (refEvent?.eventType === EventType.TOOL_PERMISSION_REQUEST) {
    const rawToolName = refEvent.notificationContext?.toolName ?? 'tool'
    const mcpFormatted = formatMcpToolForDisplay(rawToolName)
    const displayName = mcpFormatted
      ? `${mcpFormatted.toolDisplayName} ${mcpFormatted.actionDisplayName}`
      : rawToolName.toLowerCase()
    return {
      toolName: '',
      activityContext: `Processing ${displayName} tool use request`,
      status: 'user',
      isBlockingResponse: true,
      agentMode,
      pendingAcknowledgement: {
        eventType: EventType.TOOL_PERMISSION_REQUEST,
        toolName: rawToolName,
      },
    }
  }

  return {
    toolName: '',
    activityContext: 'Processing your approval',
    status: 'user',
    isBlockingResponse: true,
    agentMode,
  }
}

interface ActivityStateResult {
  readonly userActivity: FormattedUserActivity | null
  readonly agentActivity: FormattedAgentActivity | null
}

function resolveActivityState(
  formatted: FormattedAgentActivity | FormattedUserActivity,
  currentUserActivity: FormattedUserActivity | null,
): ActivityStateResult {
  if (isUserActivity(formatted)) {
    return { userActivity: formatted, agentActivity: null }
  }

  const isAgentActivity = formatted.status === 'pre_tool' || formatted.status === 'post_tool'
  const hasBlockingResponse = currentUserActivity?.isBlockingResponse
  const hasPendingAck = currentUserActivity?.pendingAcknowledgement
  const isAlreadyAcknowledged = currentUserActivity?.isAcknowledged
  const hasPendingAcknowledgement = hasBlockingResponse && hasPendingAck
  const isUserReplyToAgentReply = hasBlockingResponse && !hasPendingAck

  if (isAgentActivity && hasPendingAcknowledgement && currentUserActivity) {
    return {
      userActivity: { ...currentUserActivity, isAcknowledged: true },
      agentActivity: formatted,
    }
  }

  if (isAgentActivity && isUserReplyToAgentReply && currentUserActivity) {
    return { userActivity: currentUserActivity, agentActivity: formatted }
  }

  const shouldPreserveUserActivity = isAlreadyAcknowledged || isUserReplyToAgentReply
  return {
    userActivity: shouldPreserveUserActivity ? currentUserActivity : null,
    agentActivity: formatted,
  }
}

function isUserActivity(
  activity: FormattedAgentActivity | FormattedUserActivity,
): activity is FormattedUserActivity {
  return activity.status === 'user'
}
